import { useMemo } from "react";
import { useGetList, useTranslate } from "ra-core";
import { ResponsiveBar } from "@nivo/bar";
import { Skeleton } from "@/components/ui/skeleton";
import type { Payment } from "../../types";

const MONTHS = 12;
const monthKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

/**
 * Forecast bar chart: planned income vs planned expense for the next N months
 * (status pending+overdue, grouped by month of planned_date).
 */
export const PlannedCashflowChart = () => {
  const translate = useTranslate();
  const start = useMemo(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const startIso = start.toISOString().slice(0, 10);
  const endIso = useMemo(() => {
    const d = new Date(start);
    d.setMonth(d.getMonth() + MONTHS);
    return d.toISOString().slice(0, 10);
  }, [start]);

  const { data, isPending } = useGetList<Payment>("payments", {
    pagination: { page: 1, perPage: 5000 },
    sort: { field: "planned_date", order: "ASC" },
    filter: {
      // Все статусы: paid тоже показываем, чтобы видеть полную картину по месяцам.
      "planned_date@gte": startIso,
      "planned_date@lt": endIso,
    },
  });

  const chartData = useMemo(() => {
    const buckets: Record<string, { month: string; income: number; expense: number }> = {};
    for (let i = 0; i < MONTHS; i++) {
      const d = new Date(start);
      d.setMonth(start.getMonth() + i);
      const key = monthKey(d);
      buckets[key] = { month: key, income: 0, expense: 0 };
    }
    for (const p of data ?? []) {
      const key = monthKey(new Date(p.planned_date));
      if (!buckets[key]) continue;
      if (p.direction === "income") buckets[key].income += Number(p.amount);
      else buckets[key].expense += Number(p.amount);
    }
    return Object.values(buckets);
  }, [data, start]);

  if (isPending) return <Skeleton className="h-64 w-full" />;
  if (!chartData.length) {
    return (
      <p className="text-sm text-muted-foreground">
        {translate("finance.dashboard.noData", { _: "Нет данных" })}
      </p>
    );
  }

  return (
    <div className="h-64">
      <ResponsiveBar
        data={chartData}
        keys={["income", "expense"]}
        indexBy="month"
        margin={{ top: 16, right: 16, bottom: 40, left: 56 }}
        groupMode="grouped"
        padding={0.25}
        colors={["#16a34a", "#dc2626"]}
        axisBottom={{ tickRotation: -30 }}
        axisLeft={{ format: (v) => `${(Number(v) / 1000).toFixed(0)}k` }}
        enableLabel={false}
        animate={false}
        tooltip={({ id, value, indexValue }) => (
          <div className="rounded-md border bg-popover px-2 py-1 text-xs shadow">
            {indexValue} · {String(id)}:{" "}
            {Number(value).toLocaleString("ru-RU")} ₽
          </div>
        )}
      />
    </div>
  );
};
